import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Img from "@ucs/MobiusOverrides/Img/Img";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC } from "react";
import styled, { css, FlattenSimpleInterpolation } from "styled-components";

interface OwnProps {
    image: string;
    containerStyles?: InjectableCss;
    children?: any;
    imageExtendedStyles?: React.CSSProperties;
    imageClickHandler?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    focalPoint?:
        | "topLeft"
        | "topCenter"
        | "topRight"
        | "centerLeft"
        | "center"
        | "centerRight"
        | "bottomLeft"
        | "bottomCenter"
        | "bottomRight";
}

export function getFocalPointStyles(
    focalPoint:
        | "topLeft"
        | "topCenter"
        | "topRight"
        | "centerLeft"
        | "center"
        | "centerRight"
        | "bottomLeft"
        | "bottomCenter"
        | "bottomRight",
) {
    let focalPointStyles;
    switch (focalPoint) {
        case "topLeft":
            focalPointStyles = "top: 0; left: 0; transform-origin: top left;";
            break;
        case "topCenter":
            focalPointStyles = "top: 0; left: 50%; transform-origin: top center;";
            break;
        case "topRight":
            focalPointStyles = "top: 0; right: 0; transform-origin: top right;";
            break;
        case "centerLeft":
            focalPointStyles = "top: 50%; left: 0; transform-origin: center left;";
            break;
        case "center":
            focalPointStyles = "top: 50%; left: 50%;";
            break;
        case "centerRight":
            focalPointStyles = "right: 0; top: 50%; transform-origin: center right;";
            break;
        case "bottomLeft":
            focalPointStyles = "bottom: 0; left: 0; transform-origin: bottom left;";
            break;
        case "bottomCenter":
            focalPointStyles = "left: 50%; bottom: 0; transform-origin: bottom center;";
            break;
        case "bottomRight":
            focalPointStyles = "bottom: 0; right: 0; transform-origin: bottom right;";
            break;
    }

    return focalPointStyles;
}

export function getFocalPointTransforms(
    focalPoint:
        | "topLeft"
        | "topCenter"
        | "topRight"
        | "centerLeft"
        | "center"
        | "centerRight"
        | "bottomLeft"
        | "bottomCenter"
        | "bottomRight",
) {
    let focalPointStyles;
    switch (focalPoint) {
        case "topLeft":
            focalPointStyles = "translateX(0) translateY(0)";
            break;
        case "topCenter":
            focalPointStyles = "translateX(-50%) translateY(0)";
            break;
        case "topRight":
            focalPointStyles = "translateX(0) translateY(0)";
            break;
        case "centerLeft":
            focalPointStyles = "translateX(0) translateY(-50%)";
            break;
        case "center":
            focalPointStyles = "translate(-50%, -50%)";
            break;
        case "centerRight":
            focalPointStyles = "translateX(0) translateY(-50%)";
            break;
        case "bottomLeft":
            focalPointStyles = "translateX(0) translateY(0)";
            break;
        case "bottomCenter":
            focalPointStyles = "translateX(-50%) translateY(0)";
            break;
        case "bottomRight":
            focalPointStyles = "translateX(0) translateY(0)";
            break;
    }

    return focalPointStyles;
}

const FitImage: FC<OwnProps> = props => {
    const { image, containerStyles, focalPoint, imageClickHandler, children, imageExtendedStyles } = props;
    const focalPointStyles = getFocalPointStyles(focalPoint ?? "center");
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const imageRef = React.useRef<HTMLImageElement | null>(null);
    const cursorPointer = imageClickHandler ? "cursor: pointer;" : "";
    const [imageStyles, setImageStyles] = React.useState<{ css: FlattenSimpleInterpolation }>({
        css: css`
            ${focalPointStyles};
            position: absolute;
            width: 100%;
            height: auto;
            image-rendering: auto;
            ${cursorPointer}
        `,
    });

    const [windowWidth, setWindowWidth] = React.useState<number | null>(
        typeof window !== "undefined" ? window?.innerWidth : null,
    );
    React.useEffect(() => {
        if (typeof window !== "undefined") {
            const handleResize = () => {
                setWindowWidth(window?.innerWidth);
            };
            window?.addEventListener("resize", handleResize);
            return () => {
                window?.removeEventListener("resize", handleResize);
            };
        }
    }, []);

    const [divHeight, setHeight] = React.useState(0);
    const [divWidth, setWidth] = React.useState(0);

    React.useEffect(() => {
        const updateSize = () => {
            requestAnimationFrame(() => {
                if (containerRef?.current) {
                    setHeight(containerRef.current?.offsetHeight);
                    setWidth(containerRef.current?.offsetWidth);
                }
            });
        };

        const observer = new ResizeObserver(updateSize);

        if (containerRef?.current) {
            observer.observe(containerRef.current);
        }

        // Handle image load delay
        const images = containerRef?.current?.querySelectorAll("img");
        images?.forEach(img => img.addEventListener("load", updateSize));

        return () => {
            observer.disconnect();
            images?.forEach(img => img.removeEventListener("load", updateSize));
        };
    }, []);

    React.useEffect(() => {
        handleImageLoad();
    }, [windowWidth, image, divHeight, divWidth, focalPoint]);

    const handleImageLoad = () => {
        let getTransform = "";
        const imageInContainer = imageRef?.current;
        const loadedContainer = containerRef?.current;
        if (loadedContainer && imageInContainer) {
            const containerRatio = loadedContainer.offsetWidth / loadedContainer.offsetHeight;
            const imageRatio = imageInContainer.width / imageInContainer.height;
            let scale = 1;
            if (imageRatio > containerRatio) {
                // Image is wider than the container, zoom out (height fits first)
                scale = loadedContainer.offsetHeight / imageInContainer.height;
            } else {
                // Image is taller than the container, zoom out (width fits first)
                scale = loadedContainer.offsetWidth / imageInContainer.width;
            }
            scale = scale === Infinity ? 1 : scale;
            // Apply the scale factor
            getTransform = `scale(${scale})`;
        }

        const text = `${getFocalPointTransforms(focalPoint ?? "center")} ${getTransform}`;
        setImageStyles({
            css: css`
                ${focalPointStyles};
                transform: ${text};
                position: absolute;
                width: 100%;
                height: auto;
                image-rendering: auto;
                ${cursorPointer}
            `,
        });
    };

    return (
        <StyledWrapper {...containerStyles} ref={containerRef}>
            <Img
                src={image}
                {...imageStyles}
                refElem={imageRef}
                onLoad={handleImageLoad}
                onClick={imageClickHandler}
                style={imageExtendedStyles}
            />
            {children}
        </StyledWrapper>
    );
};

export default FitImage;
